/**
 * Ajustes.
 *
 * Incluye dos interruptores que en Cuentia **existían pero no hacían nada**: el
 * arrastre del sobrante del presupuesto y la caja chica diaria del gasto hormiga.
 * Aquí se leen de verdad.
 *
 * Y la zona de peligro, con el borrado total detrás de escribir «BORRAR», que
 * conserva la cuenta de usuario para poder volver a empezar.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Eye, EyeOff, LogOut, Moon, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { formatCOP, parseAmountFromText } from '@eldescuadre/core';
import type { Settings } from '@eldescuadre/contracts';

import { getSettings, purgeAllData, saveSettings } from '../lib/endpoints';
import { useSession } from '../lib/session';
import { ApiError } from '../lib/api';
import { THEME_LABEL, useTheme, type ThemePreference } from '../lib/theme';
import { Card, ErrorNote, Field, Loading, Modal, Note, SubmitButton } from '../components/ui';
import { QuickLockSettings } from '../components/QuickLockSettings';
import { BiometricSettings } from '../components/BiometricSettings';
import { RestoreBackup } from '../components/RestoreBackup';
import { CloudBackup } from '../components/CloudBackup';
import { BackupButton, ExportReportList } from '../components/ExportReports';

export function SettingsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const user = useSession((s) => s.user);
  const stealth = useSession((s) => s.stealth);
  const toggleStealth = useSession((s) => s.toggleStealth);
  const signOut = useSession((s) => s.signOut);

  const [purging, setPurging] = useState(false);
  const [purgeText, setPurgeText] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);

  const settings = useQuery({ queryKey: ['settings'], queryFn: getSettings });

  // El tema se aplica **al instante**, sin esperar a que el servidor confirme: es un
  // cambio visual y hacer esperar medio segundo para verlo se siente roto.
  const { setPreference } = useTheme(settings.data?.settings.theme as ThemePreference | undefined);

  const save = useMutation({
    mutationFn: saveSettings,
    onSuccess: async () => {
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 2000);
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  /** Guarda un cambio suelto sin recargar toda la pantalla. */
  const patch = (input: Parameters<typeof saveSettings>[0]): void => {
    save.mutate(input);
  };

  const purge = useMutation({
    mutationFn: purgeAllData,
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      setPurging(false);
      setPurgeText('');
      navigate('/');
    },
  });

  const purgeError = purge.error instanceof ApiError ? purge.error : null;
  const current: Settings | undefined = settings.data?.settings;

  return (
    <div className="space-y-4 animate-ed-fade-in">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Ajustes</h1>
          <p className="mt-0.5 text-sm text-ed-text-muted">{user?.email}</p>
        </div>
        {savedFlash && (
          <span className="rounded-full bg-ed-success/15 px-3 py-1 text-xs text-ed-success">
            Guardado
          </span>
        )}
      </header>

      {settings.isLoading && <Loading />}
      {save.isError && <ErrorNote message="No se pudieron guardar los ajustes." />}

      {current && (
        <>
          {/* Privacidad */}
          <Card title="Privacidad">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-ed-bg-border"
                checked={stealth}
                onChange={toggleStealth}
              />
              <span className="text-sm">
                <span className="flex items-center gap-1.5 font-medium">
                  {stealth ? <EyeOff size={14} aria-hidden="true" /> : <Eye size={14} aria-hidden="true" />}
                  Modo discreto
                </span>
                <span className="mt-0.5 block text-xs text-ed-text-muted">
                  Oculta todos los importes con puntos. Útil cuando alguien mira tu pantalla en el
                  bus o en la oficina. Se puede activar y quitar desde la cabecera.
                </span>
              </span>
            </label>
          </Card>

          {/* Presupuestos */}
          <Card title="Presupuestos">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-ed-bg-border"
                checked={current.budgetRollover}
                onChange={(e) => patch({ budgetRollover: e.target.checked })}
              />
              <span className="text-sm">
                <span className="block font-medium">Arrastrar el sobrante</span>
                <span className="mt-0.5 block text-xs text-ed-text-muted">
                  Lo que no gastes de un presupuesto se suma al mes siguiente. Si te pasas, el
                  exceso <strong>no</strong> se arrastra: no se acumula deuda.
                </span>
              </span>
            </label>

            <div className="mt-4">
              <Field
                label="Caja chica diaria para el gasto hormiga"
                htmlFor="caja-chica"
                hint="Lo que consideras razonable gastar al día en menudencias. Te avisamos cuando te pases."
              >
                <input
                  id="caja-chica"
                  className="ed-input ed-money"
                  inputMode="numeric"
                  defaultValue={String(current.hormigaDailyBudget)}
                  onBlur={(e) => {
                    const value = parseAmountFromText(e.target.value) ?? 0;
                    if (value > 0 && value !== current.hormigaDailyBudget) {
                      patch({ hormigaDailyBudget: value });
                    }
                  }}
                  autoComplete="off"
                />
              </Field>
              <p className="mt-1 text-xs text-ed-text-muted">
                Ahora mismo: <strong>{formatCOP(current.hormigaDailyBudget)}</strong> al día.
              </p>
            </div>
          </Card>

          {/* Preferencias */}
          <Card title="Preferencias">
            <div className="space-y-4">
              <Field
                label="Tema"
                htmlFor="tema"
                hint="También puedes cambiarlo desde el botón de la cabecera, en cualquier pantalla."
              >
                <select
                  id="tema"
                  className="ed-input"
                  value={current.theme}
                  onChange={(e) => {
                    const theme = e.target.value as ThemePreference;
                    // Primero se ve, después se guarda.
                    setPreference(theme);
                    patch({ theme });
                  }}
                >
                  <option value="DARK">{THEME_LABEL.DARK}</option>
                  <option value="LIGHT">{THEME_LABEL.LIGHT}</option>
                  <option value="SYSTEM">{THEME_LABEL.SYSTEM} — sigue lo que tengas puesto</option>
                </select>
              </Field>

              <Field
                label="La semana empieza en"
                htmlFor="semana"
                hint="Solo afecta a cómo se agrupan los informes semanales."
              >
                <select
                  id="semana"
                  className="ed-input"
                  value={String(current.weekStartsOn)}
                  onChange={(e) => patch({ weekStartsOn: Number(e.target.value) })}
                >
                  <option value="1">Lunes</option>
                  <option value="0">Domingo</option>
                </select>
              </Field>

              <p className="flex items-start gap-2 text-xs text-ed-text-muted">
                <Moon size={13} className="mt-0.5 shrink-0" aria-hidden="true" />
                El tema claro todavía no está afinado. El oscuro es el que se ha probado a fondo.
              </p>
            </div>
          </Card>

          {/* Datos */}
          <Card title="Llevarte tus datos">
            <p className="mb-3 text-xs text-ed-text-muted">
              Cada informe en su propio archivo, para abrirlo en Excel o pasárselo a un contador.
              Los importes van como números, sin símbolos, para que la hoja de cálculo los sume.
            </p>

            <ExportReportList />

            <div className="mt-3">
              <BackupButton />
            </div>

            <p className="mt-3 text-xs text-ed-text-muted">
              Tus datos son tuyos. Puedes llevártelos cuando quieras, en un formato que se puede
              leer sin esta aplicación.
            </p>

            {/* Sacar la copia del servidor, al menú de compartir del teléfono. Es la
                respuesta a «respaldos en la nube» sin pedirle nada a nadie. */}
            <div className="mt-4 border-t border-ed-bg-border/30 pt-4">
              <CloudBackup />
            </div>

            <div className="mt-4 border-t border-ed-bg-border/30 pt-4">
              <RestoreBackup />
            </div>
          </Card>

          {/* Bloqueo rápido con PIN. Va antes de la sesión porque es lo que protege
              el teléfono cuando alguien lo coge, que es el caso más común. */}
          <Card title="Bloqueo rápido">
            <QuickLockSettings />
          </Card>

          {/* Entrar con la huella. Es una comodidad, no una protección, y así se dice. */}
          <Card title="Entrar con la huella">
            <BiometricSettings />
          </Card>

          {/* Sesión */}
          <Card title="Sesión">
            <button
              type="button"
              className="ed-btn-secondary w-full"
              onClick={async () => {
                await signOut();
                navigate('/entrar', { replace: true });
              }}
            >
              <LogOut size={16} aria-hidden="true" />
              Cerrar sesión
            </button>
          </Card>

          {/* Zona de peligro */}
          <Card title="Zona de peligro">
            <div className="flex items-start gap-3 rounded-ed border border-ed-coral-deep/50 bg-ed-coral-deep/10 p-3">
              <AlertTriangle size={18} className="mt-0.5 shrink-0 text-ed-coral-light" aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-ed-coral-light">Borrar todos mis datos</p>
                <p className="mt-1 text-xs text-ed-text-silver">
                  Elimina todos tus movimientos, cuentas, deudas, fiado, presupuestos y metas.
                  <strong> Tu cuenta de usuario se conserva</strong>, así que podrás volver a
                  empezar sin perder el acceso. Esta acción no se puede deshacer.
                </p>
                <button
                  type="button"
                  className="ed-btn-danger mt-3 !min-h-[44px] !w-auto !px-4"
                  onClick={() => setPurging(true)}
                >
                  <Trash2 size={16} aria-hidden="true" />
                  Quiero borrar todo
                </button>
              </div>
            </div>
          </Card>
        </>
      )}

      <p className="px-1 pb-2 text-center text-xs text-ed-text-muted">
        ElDescuadre · versión 1.0.0
      </p>

      {purging && (
        <Modal
          open
          onClose={() => {
            setPurging(false);
            setPurgeText('');
          }}
          title="Borrar todos los datos"
          footer={
            <SubmitButton
              type="button"
              variant="danger"
              pending={purge.isPending}
              disabled={purgeText !== 'BORRAR'}
              onClick={() => purge.mutate()}
            >
              Borrar definitivamente
            </SubmitButton>
          }
        >
          <div className="space-y-4">
            <ErrorNote
              message="Esto borra todos tus datos financieros y no se puede deshacer."
              hint="Si quieres conservar algo, descarga antes el respaldo completo desde «Tus datos»."
            />

            <Field
              label="Escribe BORRAR para confirmar"
              htmlFor="confirmar-borrado"
              hint="Con mayúsculas, exactamente así."
            >
              <input
                id="confirmar-borrado"
                className="ed-input"
                value={purgeText}
                onChange={(e) => setPurgeText(e.target.value)}
                placeholder="BORRAR"
                autoComplete="off"
              />
            </Field>

            <Note>
              Tu cuenta de usuario y tu contraseña <strong>no</strong> se borran. Solo los datos.
            </Note>

            {purgeError && <ErrorNote message={purgeError.message} />}
          </div>
        </Modal>
      )}
    </div>
  );
}
